/* eslint-disable no-console */
import { lazy } from 'react'
import { IMPORT_ERROR_BOUNDARY_UI_KEY } from '../routes/errors/ErrorBoundaryUI'

/**
 * A utility wrapper around `React.lazy` that enhances dynamic imports with retry logic.
 * It attempts to retry failed module imports using cache-busting query parameters for the
 * import or by re-calling the importer.
 *
 * References:
 * - https://medium.com/@alonmiz1234/retry-dynamic-imports-with-react-lazy-c7755a7d557a
 * - https://stackoverflow.com/a/76200536/6136566
 * @param importer  - A dynamic import function that returns a module
 * @returns A React component wrapped in `React.lazy` with retry logic applied
 */
export const lazyWithRetries: typeof lazy = importer => {
  const retryImport = async () => {
    try {
      return await importer()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // If import retries have run and page reloaded, end cycle and show ErrorBoundaryUI
      if (window.localStorage.getItem(IMPORT_ERROR_BOUNDARY_UI_KEY) === 'true') {
        throw error
      }

      // Extract the URL from error message using regex matching
      const errorMessage: string = error?.message || ''
      const urlMatch = errorMessage.match(/https?:\/\/[^\s]+/)

      // If there is no url, retry by calling importer()
      if (!urlMatch) {
        console.warn('Failed to extract URL from error message:', errorMessage)
        for (let i = 0; i < 3; i++) {
          await new Promise(resolve => setTimeout(resolve, 1000 * 2 ** i))
          try {
            return await importer()
          } catch (e: unknown) {
            console.warn(`Retrying dynamic import (${i + 1}/3)`, e)
          }
        }
        throw error
      }

      // If there is a url, retry by calling import()
      for (let i = 0; i < 3; i++) {
        await new Promise(resolve => setTimeout(resolve, 1000 * 2 ** i))
        const url = new URL(urlMatch[0])
        // Change the url to force the browser to attempt no-cache import ("cache busting")
        url.searchParams.set('t', `${+new Date()}`)
        try {
          // Adds vite-ignore as recommended since it is intended to be left as is
          return await import(/* @vite-ignore */ url.href)
        } catch (e: unknown) {
          console.warn(`Retrying dynamic import (no-cache) (${i + 1}/3)`, e)
        }
      }
      throw error
    }
  }
  return lazy(retryImport)
}
